import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { ErrorBanner, Pill, Spinner } from "../components/common";
import { dateTime, day } from "../lib/format";

const STATUS_KIND = {
  uploaded: "neutral",
  "qc acknowledged": "info",
  curating: "info",
  complete: "ok",
};

/**
 * Prior runs, newest first. Reopening one restores its curation exactly.
 *
 * The table is about the run, not about who ran it. The operator is still parsed
 * from the Substanzen sheet, stored with the run and written to the workbook's
 * Run Info sheet; it is shown on the run's own page, where it reads as part of
 * the record rather than as ownership of a row.
 */
export default function RunsScreen() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api
      .listRuns()
      .then((data) => live && setRuns(data))
      .catch((e) => live && setError(e));
    return () => {
      live = false;
    };
  }, []);

  if (error) return <ErrorBanner error={error} />;
  if (!runs) return <Spinner label="Loading runs…" />;

  return (
    <>
      <div className="button-row" style={{ marginTop: 0, marginBottom: "1.25rem" }}>
        <button className="primary" onClick={() => navigate("/new")}>
          New run
        </button>
      </div>

      <div className="card">
        <h2>Runs</h2>
        {runs.length === 0 ? (
          <p className="muted">
            Nothing analysed yet. Upload three instrument files and a Substanzen workbook to
            start.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Experiment</th>
                  <th>Status</th>
                  <th className="num">QC flags</th>
                  <th className="num">Exclusions</th>
                  <th>Analysed</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.run_id}>
                    <td>
                      {/* A run whose QC was never acknowledged reopens at QC, not at
                          the curves -- the same gate as a fresh upload. */}
                      <Link
                        to={`/runs/${run.run_id}/${run.qc_acknowledged_at ? "curate" : "qc"}`}
                      >
                        {run.title || run.run_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td>{day(run.experiment_start)}</td>
                    <td>
                      <Pill kind={STATUS_KIND[run.status] || "neutral"}>{run.status}</Pill>
                      {!run.qc_acknowledged_at && (
                        <Pill kind="warning">QC not acknowledged</Pill>
                      )}
                    </td>
                    <td className="num">
                      {run.n_qc_flags}
                      {run.n_errors > 0 && (
                        <span style={{ color: "var(--error)" }}> ({run.n_errors} error)</span>
                      )}
                    </td>
                    <td className="num">{run.n_exclusions}</td>
                    <td className="muted">{dateTime(run.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
